import { HeroBackground, ProductMain } from '@prishapolicy/shared/ui';
import {
  ChoosePlan,
  ChoosePlanProps,
  Faq,
  HeroTicker,
  LifeMidSection,
  ProductCategoryPageProps,
  ProtectToday,
  VerticalCardsView,
  WhyChooseUs,
  getPurchaseFlowHeroForms,
  lifeFaqList,
  productsPathAdder,
  useW3PurchaseFlow,
} from '@prishapolicy/w3/ui';
import { NextSeo } from 'next-seo';
import React from 'react';
const productCategorySlug = 'life-insurance';
import familyIcon from './familyIcon.png';
import endownment from './endownment.png';
import permanent from './permanent.png';
import unitLinked from './unitLinked.png';
import termWaiting from './termWaiting.png';
import Image from 'next/image';

import RestartFillIcon from 'remixicon-react/RestartFillIcon';
import QuillPenFillIcon from 'remixicon-react/QuillPenFillIcon';
import Dashboard2FillIcon from 'remixicon-react/Dashboard2FillIcon';

export function LifeInsurance(props: ProductCategoryPageProps) {
  const choosePlanCards: ChoosePlanProps['cards'] = [
    {
      title: 'Assess Your Coverage Needs',
      content: (
        <div>
          Begin by evaluating your financial responsibilities, including outstanding debts, future expenses such as
          education or mortgage payments, and the financial needs of your dependents. Determine the amount of coverage
          required to adequately protect your loved ones in case of unforeseen events.
        </div>
      ),
    },
    {
      title: 'Understand Policy Features',
      content: (
        <div>
          Take the time to understand the features and terms of different life insurance policies available in the
          market. Compare factors such as premium amounts, coverage duration, payout options (e.g., lump sum or
          installment), and additional benefits or riders offered (e.g., critical illness coverage, accidental death
          benefit).
        </div>
      ),
    },
    {
      title: 'Check Claim Settlement Record ',
      content: (
        <div>
          Look for insurers with a high claim settlement ratio, indicating their reliability in honoring claims and
          providing timely payouts to policyholders. A strong track record in this regard ensures that your
          beneficiaries will receive the financial support they need when the time comes.
        </div>
      ),
    },
    {
      title: 'Assess Insurer&apos;s Financial Stability',
      content: (
        <div>
          Check their credit ratings from reputable agencies to ensure they have the resources to fulfill their
          obligations over the long term. A financially stable insurer provides assurance that your policy benefits will
          be secure and paid out as promised, offering peace of mind to you and your loved ones.
        </div>
      ),
    },
  ];
  const { IconRenderer, FormRenderer } = useW3PurchaseFlow(props);
  return (
    <>
      <NextSeo
        title="Life Insurance | PrishaPolicy"
        description="Secure your loved ones' future effortlessly. Explore tailored life insurance plans at PrishaPolicy."
      />
      <ProductMain color="teal">
        <HeroBackground backgroundColor="teal-300" PurchaseFlowFormRenderer={FormRenderer}>
          <div className=" grid-cols-2 xl:grid">
            <div className="flex flex-col justify-between">
              <div className="flex flex-col gap-2">
                <h1 className="text-title-3 lg:text-title-1 text-center font-bold lg:text-left">
                  Empowering Lives,
                  <br /> Ensuring Legacies: Your
                  <br /> Shield in Uncertain Times.
                </h1>
                <p className="text-title-7 lg:text-title-6 text-center font-normal lg:text-left">
                  Secure your loved ones future effortlessly. Explore tailored life insurance plans at PrishaPolicy.
                </p>
              </div>
              <IconRenderer />
            </div>
            <div className="hidden w-full justify-center xl:flex">
              <div className="mt-28 flex  justify-center">
                <Image
                  src={familyIcon}
                  alt="family Icon"
                  style={{
                    objectFit: 'contain',
                  }}
                />
              </div>
            </div>
          </div>
        </HeroBackground>
        <HeroTicker />
        <ProtectToday productCategoryPrefix={productCategorySlug} maxPerRow={4} items={props.productPaths} />
        <div className="container py-16">
          <LifeMidSection />
        </div>
        <ChoosePlan perRow={4} cards={choosePlanCards} />
        <div className="py-18">
          <div className="container">
            <h1 className="text-title-5 lg:text-title-3 mb-4 text-left font-bold lg:text-center">
              Different Types of Life Insurance?
            </h1>
            <p className="text-title-7 lg:text-title-6  text-left font-medium  lg:text-center">
              While third-party coverage is essential for driving, it is not enough. Comprehensive coverage includes
              <br />
              protection from both natural and man-made disasters like storms, earthquakes, theft, and mob unrest. Also,{' '}
              <br />
              check for cashless facilities in these plans or the ease of not paying money upfront.
            </p>
          </div>
          <VerticalCardsView
            carouselItems={[
              {
                text: 'Your trusted companion through life&apos;s twists and turns. Providing security with every step, it ensures your loved ones are shielded from life&apos;s uncertainties, offering peace of mind without breaking the bank.',
                icon: RestartFillIcon,
                heading: 'Term Life Insurance',
                rightContent: (
                  <div className="hidden place-items-center lg:grid">
                    <Image src={termWaiting} alt="Term Life insurance" />
                  </div>
                ),
              },
              {
                text: `More than just a policy, it's a promise. Embrace the journey of protection and prosperity, as each premium payment paves the way for a brighter future, securing your dreams with every contribution.`,
                icon: QuillPenFillIcon,
                heading: 'Endowment Insurance',
                rightContent: (
                  <div className="hidden place-items-center lg:grid">
                    <Image src={endownment} alt="Endowment insurance" />
                  </div>
                ),
              },
              {
                text: `Your steadfast partner for life's journey. With every premium, it builds a fortress of security, ensuring your legacy endures, and your loved ones thrive, offering a lifetime of protection and peace.`,
                icon: Dashboard2FillIcon,
                heading: 'Permanent/Whole Life Insurance',
                rightContent: (
                  <div className="hidden place-items-center lg:grid">
                    <Image src={permanent} alt="Permanent/Whole Life insurance" />
                  </div>
                ),
              },
              {
                text: `Unleash the potential of life's possibilities. Dynamic, flexible, and empowering, it's not just insurance; it's your passport to financial freedom, guiding you towards a future filled with promise and prosperity.`,
                icon: Dashboard2FillIcon,
                heading: 'Unit Linked Plans',
                rightContent: (
                  <div className="hidden place-items-center lg:grid">
                    <Image src={unitLinked} alt="Permanent/Whole Life insurance" />
                  </div>
                ),
              },
            ]}
          />
        </div>
        <WhyChooseUs />
        <Faq list={lifeFaqList} />
      </ProductMain>
    </>
  );
}

export function getStaticProps() {
  const productPaths = productsPathAdder(productCategorySlug);
  return {
    props: {
      ...getPurchaseFlowHeroForms(productCategorySlug).props,
      productPaths,
    },
  };
}

export default LifeInsurance;
